import React from 'react'
import PageTitle from '../../_Components/Common/PageTitle/PageTitle'
import {
  usePeriodData,
  PeriodControls,
  GrowthValue,
  num,
  percent,
  Wrapper,
  Message,
  TableBox,
  Table,
  Th,
  Td,
  Name,
  Note,
  Muted,
  Footnote,
} from './dataShared'

/*
 * 스티팝 이미지·영상 생성 기능별 소비를 본다.
 * 집계는 adminapi 의 /v2/admin-data/image-video 가 전부 담당하고 여기서는 표시만 한다.
 * 주소가 상대 경로인 것은 어드민 서버가 adminapi 로 프록시하기 때문이다 (구글 토큰 인증).
 */
const URL = '/new/webAdmin/v2/admin-data/image-video'

const StipopImageVideoData = () => {
  const state = usePeriodData({
    url: URL,
    // 토큰은 로그인 시점에 채워지므로 요청할 때 읽는다.
    headers: () => ({ token: window.sessionStorage.getItem('token') }),
    unwrap: data => data,
  })
  const { body, loading, error, prevLabel } = state
  const list = body.list || []

  // 기능마다 유저가 겹치므로 유저 수는 더하지 않는다. 코인과 건수만 합이 뜻을 가진다.
  const totalSpend = list.reduce((sum, row) => sum + (row.spend || 0), 0)
  const totalCount = list.reduce((sum, row) => sum + (row.count || 0), 0)

  const renderRow = row => (
    <tr key={row.id}>
      <Td align="left">
        <Name>{row.name}</Name>
        <Note>{row.media === 'video' ? '영상' : '이미지'}</Note>
      </Td>

      <Td align="left">
        {row.available ? (
          <>
            {(row.models || []).map(model => (
              <div key={model}>{model}</div>
            ))}
            <Note>{row.platform}</Note>
          </>
        ) : (
          <Muted>-</Muted>
        )}
      </Td>

      {!row.available ? (
        <Td colSpan={10} align="left">
          <Muted>{row.note}</Muted>
        </Td>
      ) : (
        <>
          <Td>
            <strong>{num(row.spend)}</strong>
            {row.spendUnit === null && <Note>무과금</Note>}
          </Td>
          <Td>{num(row.users)}</Td>
          <Td>{num(row.count)}</Td>
          <Td>
            {num(row.errors)}
            {/* 다른 모델로 다시 시도해 결국 이미지가 나온 건은 오류가 아니라 재시도로 뺀다 */}
            {row.retries !== null && row.retries !== undefined && (
              <Note>재시도 {num(row.retries)}</Note>
            )}
            {!!row.pending && <Note>대기 {num(row.pending)}</Note>}
          </Td>
          <Td>{percent(row.successRate)}</Td>
          <Td>{num(row.avgPerUser)}</Td>
          <Td>{num(row.repeatUsers)}</Td>
          <Td>{num(row.maxPerUser)}</Td>
          <Td>
            <GrowthValue value={row.growth && row.growth.count} />
          </Td>
        </>
      )}
    </tr>
  )

  /// 모델 분포와 세부 내역은 칸이 좁아 표 아래에 따로 편다.
  const detailed = list.filter(row => (row.modelMix || []).length || (row.breakdown || []).length)

  return (
    <Wrapper>
      <PageTitle
        mainTitle={'Stipop Image/Video Data'}
        v1Link={''}
        navNames={['Data Analytics', 'Stipop Image/Video Data']}
      />

      <PeriodControls
        state={state}
        summary={
          !!list.length && (
            <span>
              생성 {num(totalCount)}건 · 소비 {num(totalSpend)}코인
            </span>
          )
        }
      />

      {error && <Message>{error}</Message>}
      {loading && !error && <Message>불러오는 중…</Message>}

      {!loading && !error && (
        <>
          <TableBox>
            <Table>
              <thead>
                <tr>
                  <Th align="left">기능</Th>
                  <Th align="left">모델</Th>
                  <Th>소비 코인</Th>
                  <Th>유저 수</Th>
                  <Th>생성 건수</Th>
                  <Th title="실패해서 코인이 환불된 건. 재시도로 살아난 건은 뺐다.">오류</Th>
                  <Th title="성공 / (성공 + 오류)">성공률</Th>
                  <Th>인당 생성</Th>
                  <Th title="그 기간에 2건 이상 만든 유저">재사용 유저</Th>
                  <Th>최다 유저</Th>
                  <Th title={prevLabel && `직전 기간 ${prevLabel}`}>직전 기간 대비</Th>
                </tr>
              </thead>
              <tbody>
                {list.map(renderRow)}
                {!list.length && (
                  <tr>
                    <Td colSpan={11}>
                      <Muted>이 기간에 생성된 이미지·영상이 없습니다.</Muted>
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableBox>

          {!!detailed.length && (
            <TableBox style={{ marginTop: 12 }}>
              <Table>
                <thead>
                  <tr>
                    <Th align="left">기능</Th>
                    <Th align="left">세부 내역</Th>
                    <Th align="left">모델 분포</Th>
                  </tr>
                </thead>
                <tbody>
                  {detailed.map(row => (
                    <tr key={row.id}>
                      <Td align="left">
                        <Name>{row.name}</Name>
                      </Td>
                      <Td align="left">
                        {(row.breakdown || []).length ? (
                          row.breakdown.map(item => (
                            <div key={`${item.label}/${item.detail}`}>
                              {item.label} {num(item.count)}
                              <Note>{item.detail}</Note>
                            </div>
                          ))
                        ) : (
                          <Muted>-</Muted>
                        )}
                      </Td>
                      <Td align="left">
                        {(row.modelMix || []).map(mix => (
                          <div key={`${mix.model}/${mix.platform}`}>
                            {mix.model} {num(mix.count)}
                            <Note>{mix.platform}</Note>
                          </div>
                        ))}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableBox>
          )}
        </>
      )}

      <Footnote>
        기능마다 기록이 남는 곳이 달라 소비 코인을 읽는 방법도 다릅니다. 채팅 무드샷과
        이미지 편집은 단가가 코드에 고정돼 있어 「건수 × 단가」(무드샷 3코인, 편집 2코인)이고,
        코믹과 모션 영상은 컷 수·길이에 따라 금액이 달라 기록에 적힌 실제 청구액을 씁니다.
        캐릭터 프로필 이미지는 무료 제공분이 섞여 있어 코인 원장의 실제 차감액을 씁니다.
        리얼톡 이미지는 무과금이고 유저를 되짚을 경로가 없어 건수만 봅니다. 채팅 무드샷은
        새 이미지와 이어그리기를 합쳐 한 줄로 뒀습니다 — 실패가 코인 환불로만 남고 그
        환불에는 둘을 가를 단서가 없어, 나누면 오류와 성공률이 어느 쪽에 붙어도 틀린 값이
        되기 때문입니다. 구성은 아래 「세부 내역」에서 봅니다. 오류는 실패해서 코인이 환불된
        건이고, 한 모델이 실패한 뒤 다른 모델로 이미지가 나온 건은 오류가 아니라 재시도로
        따로 셉니다. 유저 수는 기능별로 따로 세기 때문에 여러 기능을 쓴 유저는 각 줄에 모두
        잡힙니다 — 행을 더해도 전체 유저 수와 같지 않습니다. 「직전 기간 대비」는 바로 앞의
        같은 길이 기간{prevLabel ? `(${prevLabel})` : ''}과 비교한 생성 건수 증감입니다.
      </Footnote>
    </Wrapper>
  )
}

export default StipopImageVideoData
